import { Component, HostListener, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FranchiseModel } from 'src/app/models/franchise.model';
import { SupplierService } from 'src/app/services/supplier.services';
import { ToastService } from 'src/app/services/toast.service';
import { WorkloadsService } from 'src/app/services/workloads.service';
import { NgClass } from '@angular/common';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';

@Component({
    selector: 'app-franchise-files-uploader',
    templateUrl: './franchise-files-uploader.component.html',
    styleUrls: ['./franchise-files-uploader.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, NgClass, MatButtonModule, MatIconModule, PageHeaderComponent]
})
export class FranchiseFilesUploaderComponent {
  private readonly workloadsSrv = inject(WorkloadsService);
  private readonly supplierSrv = inject(SupplierService);
  toastr = inject(ToastService);

  franchises: FranchiseModel[] = [];
  franchiseId: string = '';
  franchiseSupplierId: string = '';
  showClientFranchisError: boolean = false;
  showSupplierFranchisError: boolean = false;

  csvClientForm = new UntypedFormGroup({
    csvClientFile: new UntypedFormControl(null, Validators.required),
  });

  csvSupplierForm = new UntypedFormGroup({
    csvSupplierFile: new UntypedFormControl(null, Validators.required),
  });
  clientErrorMessage: string = '';
  clientSuccessMessage: string = '';

  supplierErrorMessage: string = '';
  supplierSuccessMessage: string = '';

  dragAreaClass: string = 'dragarea';

  ngOnInit() {
    this.workloadsSrv.getAllFranchises().subscribe((data) => {
      this.franchises = data;
    });
  }

  franchiseSelected() {
    this.showClientFranchisError = this.franchiseId != '' ? false : true;
  }

  franchiseSupplierSelected() {
    this.showSupplierFranchisError =
      this.franchiseSupplierId != '' ? false : true;
  }

  @HostListener('dragover', ['$event']) onDragOver(event: any) {
    event.preventDefault();
    this.dragAreaClass = 'droparea';
  }

  @HostListener('dragleave', ['$event']) onDragLeave(event: any) {
    event.preventDefault();
    this.dragAreaClass = 'dragarea';
  }

  onClientFileDrop(event: DragEvent) {
    event.preventDefault();
    this.dragAreaClass = 'dragarea';
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.onClientFileChange({ target: { files: files } });
    }
  }

  onSupplierFileDrop(event: DragEvent) {
    event.preventDefault();
    this.dragAreaClass = 'dragarea';
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.onSupplierFileChange({ target: { files: files } });
    }
  }

  removeClientFile() {
    this.csvClientForm.patchValue({ csvClientFile: null });
  }

  removeSupplierFile() {
    this.csvSupplierForm.patchValue({ csvSupplierFile: null });
  }

  onClientFileChange(event: any) {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.csvClientForm.patchValue({ csvClientFile: file });
      this.clientErrorMessage = '';
    } else {
      this.clientErrorMessage = 'Por favor seleccione un archivo CSV válido.';
      this.csvClientForm.patchValue({ csvClientFile: null });
    }
  }

  onSupplierFileChange(event: any) {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      this.csvSupplierForm.patchValue({ csvSupplierFile: file });
      this.supplierErrorMessage = '';
    } else {
      this.supplierErrorMessage = 'Por favor seleccione un archivo CSV válido.';
      this.csvSupplierForm.patchValue({ csvSupplierFile: null });
    }
  }

  submitClients() {
    if (this.csvClientForm.invalid) {
      this.clientErrorMessage = 'Por favor seleccione un archivo CSV.';
      return;
    }

    if (!this.franchiseId) {
      this.showClientFranchisError = true;
      return;
    }

    const formData = new FormData();
    formData.append('file', this.csvClientForm.get('csvClientFile')?.value);

    this.workloadsSrv.uploadClients(this.franchiseId, formData).subscribe({
      next: (response) => {
        this.toastr.success(
          'Archivo enviado correctamente',
          'Se ha enviado el archivo de clientes correctamente.'
        );
        this.csvClientForm.reset();
        this.csvClientForm.patchValue({ csvClientFile: null });
        this.franchiseId = '';
      },
      error: (err) => {
        this.toastr.error(
          'Archivo no enviado',
          'Hubo un error al enviar el archivo de clientes. Revise el archivo e inténtelo nuevamente.'
        );
        if (Array.isArray(err.error)) {
          err.error.forEach((e: any) => console.error('Error: ', e.message));
        } else {
          console.error('Error: ', err.error);
        }
        this.csvClientForm.reset();
        this.csvClientForm.patchValue({ csvClientFile: null });
      },
    });
  }

  submitSuppliers() {
    if (this.csvSupplierForm.invalid) {
      this.supplierErrorMessage = 'Por favor seleccione un archivo CSV.';
      return;
    }

    if (!this.franchiseSupplierId) {
      this.showSupplierFranchisError = true;
      return;
    }

    const formData = new FormData();
    formData.append('file', this.csvSupplierForm.get('csvSupplierFile')?.value);

    this.supplierSrv
      .uploadSuppliers(this.franchiseSupplierId, formData)
      .subscribe({
        next: (response) => {
          this.toastr.success(
            'Archivo enviado correctamente',
            'Se ha enviado el archivo de prestadores correctamente.'
          );
          this.franchiseSupplierId = '';
          this.csvSupplierForm.reset();
          this.csvSupplierForm.patchValue({ csvSupplierFile: null });
        },
        error: (err) => {
          this.toastr.error(
            'Archivo no enviado',
            'Hubo un error al enviar el archivo de prestadores. Revise el archivo e inténtelo nuevamente.'
          );
          if (Array.isArray(err.error)) {
            err.error.forEach((e: any) => console.error('Error: ', e.message));
          } else {
            console.error('Error: ', err.error);
          }
          this.csvSupplierForm.reset();
          this.csvSupplierForm.patchValue({ csvSupplierFile: null });
        },
      });
  }
}
